import { MagnifyingGlass } from '@creditornot/cb-icons';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Input } from '@creditornot/cb-input';
import { Empty } from '@creditornot/cb-placeholders';
import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { DiscountedItem } from 'modules/campaigns/hooks/useDiscountedItems';

import { CategoryAccordionItem } from './CategoryAccordionItem';
import { DiscountedItemRow } from './DiscountedItemRow';
import { groupDiscountedItemsByCategory } from './utils';

type DiscountedItemsModalContentProps = {
  menuItems: DiscountedItem[];
  selectedItemIds?: string[];
  onSelectionChange?: (itemIds: string[]) => void;
};

const Container = styled.div`
  min-height: 100%;
  display: flex;
  flex-direction: column;
`;

const SearchInputContainer = styled.div`
  padding: 16px;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const StyledEmpty = styled(Empty)`
  margin-block: 24px;
  flex-grow: 1;
`;

export const DiscountedItemsModalContent = ({
  menuItems,
  selectedItemIds,
  onSelectionChange,
}: DiscountedItemsModalContentProps) => {
  const { formatMessage } = useIntl();

  const [searchValue, setSearchValue] = useState('');
  const filteredItems = useMemo(
    () =>
      menuItems.filter(
        option =>
          option.name.toLowerCase().includes(searchValue.toLowerCase()) ||
          option.category?.name.toLowerCase().includes(searchValue.toLowerCase()),
      ),
    [menuItems, searchValue],
  );

  const itemsByCategory = useMemo(() => groupDiscountedItemsByCategory(filteredItems), [filteredItems]);

  return (
    <Container>
      <SearchInputContainer>
        <Input
          icon={<MagnifyingGlass />}
          onClearClick={() => setSearchValue('')}
          onChange={e => setSearchValue(e.target.value)}
          value={searchValue}
          placeholder={formatMessage({ id: 'campaigns.form.item.search-for-item' })}
        />
      </SearchInputContainer>
      {itemsByCategory.map(category => (
        <CategoryAccordionItem
          key={`category-${category.id}`}
          id={`category-${category.id}`}
          title={category.name}
          checked={category.items.every(item => selectedItemIds?.includes(item.id))}
          partiallyChecked={category.items.some(item => selectedItemIds?.includes(item.id))}
          onCheckboxClick={
            onSelectionChange && selectedItemIds
              ? selectAll => {
                  // Make the change by filtering menu items to keep their order
                  const newSelectedItems = selectAll
                    ? menuItems.filter(
                        menuItem =>
                          selectedItemIds.includes(menuItem.id) || category.items.some(item => item.id === menuItem.id),
                      )
                    : menuItems.filter(
                        menuItem =>
                          selectedItemIds.includes(menuItem.id) &&
                          !category.items.some(item => item.id === menuItem.id),
                      );
                  onSelectionChange(newSelectedItems.map(menuItem => menuItem.id));
                }
              : undefined
          }
        >
          {category.items.map(item => (
            <DiscountedItemRow
              key={item.id}
              item={item}
              selected={selectedItemIds?.includes(item.id)}
              onSelect={
                onSelectionChange && selectedItemIds
                  ? () => {
                      if (selectedItemIds.includes(item.id)) {
                        onSelectionChange(selectedItemIds.filter(itemId => itemId !== item.id));
                      } else {
                        const newIds = [...selectedItemIds, item.id];
                        // Keep the menu items order
                        const sortedIds = menuItems
                          .filter(menuItem => newIds.includes(menuItem.id))
                          .map(menuItem => menuItem.id);
                        onSelectionChange(sortedIds);
                      }
                    }
                  : undefined
              }
            />
          ))}
        </CategoryAccordionItem>
      ))}
      {!filteredItems.length && <StyledEmpty title={formatMessage({ id: 'campaigns.form.item.no-item' })} />}
    </Container>
  );
};
